import { getConfig } from '@sortlist-frontend/config';

import { DomainInfo } from '_backend/integration/domain-info';
import { BriefingCTA } from '_components/Briefing/types';
import { NormalBriefingProps } from '_components/Briefing/versions/NormalBriefing/types';
import { useNavigationContext } from '_core/context/navigation-context';
import { usePublicAppContext } from '_core/context/public-app-context';
import { omitInternalQueryParams } from '_core/utils/url/publicQuery';

export interface BriefingTarget {
  cta?: BriefingCTA;
  expertiseId?: number;
}

interface BriefingUrlParams {
  search: string;
  encodedCookies?: string;
}

const NON_ASCII_CHARACTER = /[\u0080-\uffff]/g;

export const encodeBriefingProps = (props: NormalBriefingProps) =>
  btoa(
    JSON.stringify(props).replace(
      NON_ASCII_CHARACTER,
      (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`,
    ),
  );

export const getBriefingPageUrl = (appUrl: string | undefined, locale: string) => {
  const appDomainInfo = DomainInfo.getFromOrigin(appUrl ?? null);
  if (appDomainInfo?.getLocales()?.includes(locale) !== true) return `${appUrl}/new-briefing`;

  return `${appUrl}${appDomainInfo.getLocalizedPath(locale)}/new-briefing`;
};

export const useBriefingUrl = () => {
  const { publicEnv } = getConfig();
  const { query } = useNavigationContext();
  const { domainInfo, briefingOptions, locale } = usePublicAppContext();
  const { expertise, skills, placeId, address, iso31661, page, skillObjects, modifiers } = briefingOptions ?? {};

  const currency = domainInfo?.getCurrency() ?? 'EUR';
  const defaultIso31661 = domainInfo?.getIso31661();
  const { placeId: defaultPlaceId, address: defaultAddress } = domainInfo?.getLocation(locale) ?? {};

  return (target: BriefingTarget, params: BriefingUrlParams) => {
    const briefingProps: NormalBriefingProps = {
      page,
      address: address ?? defaultAddress,
      placeId: placeId ?? defaultPlaceId,
      expertise: target.expertiseId != null ? Number(target.expertiseId) : expertise,
      locale,
      currency,
      iso31661: iso31661 ?? defaultIso31661 ?? undefined,
      skills,
      projectSkills: skillObjects,
      open: true,
      queryParams: omitInternalQueryParams(query),
      modifiers,
      cta: target.cta,
    };

    const urlParams = new URLSearchParams(params.search);
    urlParams.append('briefingProps', encodeBriefingProps(briefingProps));
    if (params.encodedCookies != null) {
      urlParams.append('encodedCookies', params.encodedCookies);
    }

    return `${publicEnv.appUrl}/new-briefing?${urlParams.toString()}`;
  };
};
